import moment from 'moment'
import React from 'react'
import {useHistory} from 'react-router-dom'
import clsx from 'clsx'
import { makeStyles, withStyles} from 'external/material';
import Box from '@mui/material/Box'
import MuiTabs from '@mui/material/Tabs'
import MuiTab from '@mui/material/Tab'
import InfoBar from './info-bar'
import SelectStudentsTab from './select-students'
import WriteCommentsTab from './write-comments'
import {createPost} from 'helpers/command'
import Dialog from '../../components/responsive-fullscreen-dialog'

const useDialogStyles = makeStyles(theme => ({
  paper: {
    width: '100%',
    padding: '20px 0',
    height: 'calc(100% - 100px)'
  },
}))

const useDialogActionsStyles = makeStyles({
  root: {
    padding: 0
  }
})

const useDialogTitleStyles = makeStyles({
  root: {
    padding: 0
  }
})

const useDialogContentStyles = makeStyles({
  root: {
    padding: 0,
    display: 'flex'
  }
})

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    color: '#4f576e', flexGrow: 1,
  },
}))

const Tabs = withStyles({
  root: {
    flexShrink: 0,
    borderBottom: 'solid 1px #dbe3e6',
  },
  indicator: {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    flexGrow: 1
  },
  flexContainer: {
    flexGrow: 1
  }
})((props) => <MuiTabs {...props} TabIndicatorProps={{ children: <div /> }} />)

const Tab = withStyles((theme) => ({
  root: {
    padding: '0 5px',
    margin: 0,
    maxWidth: 'initial',
    flexShrink: 1,
    flexGrow: 1,
    textTransform: 'none',
    fontWeight: theme.typography.fontWeightRegular,
    backgroundColor: '#f8f8f8',
    fontSize: theme.typography.pxToRem(18),
    [theme.breakpoints.down('md')]: {
      fontSize: theme.typography.pxToRem(15),
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.pxToRem(12),
    },
    '&:first-child': {
      borderRight: 'solid 1px #dbe3e6',
    },
    '&:focus': {
      opacity: 1,
    },
  },
  wrapper: {
    flexDirection: 'row'
  },
  selected: {
    opacity: 1,
    backgroundColor: 'transparent',
  }
}))((props) => <MuiTab disableRipple icon={<CircleIcon {...props} />} {...props} />);

const useCircleStyles = makeStyles(theme => ({
  root: {
    borderRadius: 50,
    flexShrink: 0,
    color: '#fff',
    width: '25px',
    height: '25px',
    fontSize: '11pt',
    lineHeight: '1.8',
    marginRight: '20px',
    backgroundColor: '#a6a6a6',
    [theme.breakpoints.down('sm')]: {
      marginRight: '8px',
    }
  },
  selected: {
    backgroundColor: '#e27a1b'
  }
}))

function CircleIcon(props) {
  const classes = useCircleStyles()
  return (
    <Box className={clsx(classes.root, {[classes.selected]: props.selected})}>
      {props.value + 1}
    </Box>
  )
}

const initialState = {
  group: '',
  start: moment().startOf('day').toDate(),
  finish: moment().endOf('day').toDate(),
  maxDuration: moment().endOf('day').diff(moment(), 'seconds'),
  duration: 3600,
  comments: '',
  students: [],
  selectedStudents: [],
  checkedStudents: {},
  tab: 0
}

export default function Assign ({context}) {

  const history = useHistory()
  const {translate, quizAssignmentToModify: assignment, quizDialogs, groups} = context
  const classes = useStyles()
  const dialogClasses = useDialogStyles()
  const dialogActionsClasses = useDialogActionsStyles()
  const dialogTitleClasses = useDialogTitleStyles()
  const dialogContentClasses = useDialogContentStyles()

  const isOpen = quizDialogs['edit-assignment'] !== false && assignment.id != undefined

  const [state, setState] = React.useState(initialState)

  function handleTimeChange ({hours, minutes}) {
    const duration = Math.min(minutes * 60 + hours * 3600, state.maxDuration)
    setState({...state, duration})
  }

  function handleStartChange (start) {
    const finish = moment(state.finish).isAfter(start)
    ? state.finish
    : moment(start).endOf('day').toDate()
    const maxDuration = moment(finish).diff(moment(start), 'seconds')
    const duration = Math.min(state.duration, maxDuration)
    setState({...state, start, finish, duration, maxDuration})
  }

  function handleFinishChange (finish) {
    const maxDuration = moment(finish).diff(moment(state.start), 'seconds')
    const duration = Math.min(state.duration, maxDuration)
    setState({...state, finish, duration, maxDuration})
  }

  function handleTabChange(e, tab) {
    setState({...state, tab})
  }

  async function handleCancel() {
    await context.updateState({quizDialogs: {['edit-assignment']: {$set: false}}})
    setState(initialState)
  }

  function handleContinue() {
    setState({...state, tab: 1})
  }

  function handleBack() {
    setState({...state, tab: 0})
  }

  function handleGroupChange(e) {
    const group = groups.find(g => g.id == e.target.value) || {}
    const students = group.students || []
    const checkedStudents = students.reduce((acc, student) => {
      acc[student.id] = false
      return acc
    }, {})
    setState({...state, group: e.target.value, students, selectedStudents: [], checkedStudents})
  }

  function handleStudentsChecked(id, value) {
    setState({...state, checkedStudents: {...state.checkedStudents, [id]: value}})
  }

  function handleStudentsChange({left, right}) {
    setState({...state, students: left, selectedStudents: right})
  }

  function handleCommentsChange(e){
    setState({...state, comments: e.target.value})
  }

  async function handleSend() {
    try {
      const {id} = assignment
      const {comments, duration} = state 
      const start = moment(state.start).toISOString()
      const finish = moment(state.finish).toISOString()
      const students = state.selectedStudents.map(s => s.id)
      const result = await createPost('quiz', 'updateAssignment')({
        id, start, finish, duration, comments, students 
      })
      context.setState({quizAssignmentCalls: context.quizAssignmentCalls + 1})
      handleCancel()
    } catch (e) {
      console.log(e)
      handleCancel()
    }
  }

  React.useEffect(() => {
    if (assignment.id != undefined) {
      const {group, duration, properties} = assignment
      const {comments} = properties
      const start = moment(assignment.start).toDate()
      const finish = moment(assignment.finish).toDate()
      const {students: groupStudents = []} = context.groups.find(g => g.id == group) || {}
      const students = groupStudents.filter(s => assignment.students.indexOf(s.id) < 0)
      const selectedStudents = groupStudents.filter(s => assignment.students.indexOf(s.id) > -1)
      const checkedStudents = groupStudents.reduce((acc, s) => {
        acc[s.id] = false
        return acc
      }, {})
      setState({...state, group, duration, comments, start, finish, students, checkedStudents, selectedStudents})
    }
  }, [assignment])

  return (
    <Dialog
      open={isOpen}
      close={handleCancel}
      dialogClasses={dialogClasses}
      contentClasses={dialogContentClasses}
      headClasses={dialogTitleClasses}
      actionsClasses={dialogActionsClasses}
      dialogMaxWidth="lg"
      TitleComponent={() => <InfoBar context={context}/>}
    >
      <Box className={classes.root} maxWidth="lg">
          <Tabs value={state.tab} onChange={handleTabChange}>
            <Tab label={translate('select-students-tab')}/>
            <Tab label={translate('comments-tab')}/>
          </Tabs>
          {state.tab == 0 && (
            <SelectStudentsTab
              selectedGroup={state.group}
              students={state.students}
              selectedStudents={state.selectedStudents}
              checkedStudents={state.checkedStudents}
              onGroupChange={handleGroupChange}
              onStudentsChecked={handleStudentsChecked}
              onStudentsChange={handleStudentsChange}
              onCancel={handleCancel}
              onContinue={handleContinue}
              context={context} 
            />)}
          {state.tab == 1 && (
            <WriteCommentsTab
              selectedStudents={state.selectedStudents}
              newComments={state.comments}
              start={state.start}
              finish={state.finish}
              duration={state.duration}              
              onCommentsChange={handleCommentsChange}
              onStartChange={handleStartChange}
              onFinishChange={handleFinishChange}
              onTimeChange={handleTimeChange}
              onCancel={handleCancel}
              onSend={handleSend} 
              context={context}
            />)}
      </Box>
    </Dialog>
  )
}
